import { DataLoader, PageRequest } from '@aiz-gl/omni-ui';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable, resource } from '@angular/core';
import { firstValueFrom, lastValueFrom, map, Observable } from 'rxjs';
import {
  User,
  UserParams,
  UserResponse,
  UserUpdateResponse,
} from '../../models/user.models';
import { mapUserResponseToModel, mapUserToParams } from './user.mapper';

type UserSearchApiResponse =
  | UserResponse[]
  | {
      items?: UserResponse[];
      data?: UserResponse[];
      total_count?: number;
      totalCount?: number;
      page_number?: number;
      pageNumber?: number;
      page_size?: number;
      pageSize?: number;
    };

type UserSearchPage = {
  users: User[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  USER_API_URL = 'user';
  http = inject(HttpClient);

  users$ = this.http
    .get<UserResponse[]>(`user`)
    .pipe(
      map((response: UserResponse[]) =>
        response.map((r) => mapUserResponseToModel(r)),
      ),
    );

  activeUsers$ = this.users$.pipe(
    map((users: User[]) => users.filter((u) => u.active !== false)),
  );

  inactiveUsers$ = this.users$.pipe(
    map((users: User[]) => users.filter((u) => u.active === false)),
  );

  users = resource({
    loader: (): Promise<User[]> => lastValueFrom(this.activeUsers$),
  });

  getUser$ = (email: string) =>
    this.http
      .get<UserResponse>(`user/${encodeURIComponent(email)}`)
      .pipe(map((response: UserResponse) => mapUserResponseToModel(response)));
  searchUsersPage$(
    search: string,
    pageNumber = 0,
    pageSize = 15,
    includeInactive = false,
  ): Observable<UserSearchPage> {
    const term = search.trim();
    const payload = {
      pageNumber,
      pageSize,
      sortBy: 'firstName',
      sortDirection: 'asc',
      ...(term
        ? {
            firstName: term,
            lastName: term,
            email: term,
          }
        : {}),
    };

    return this.http.post<UserSearchApiResponse>('user/search', payload).pipe(
      map((response) => {
        const rows: UserResponse[] = Array.isArray(response)
          ? response
          : response.items || response.data || [];

        const mappedUsers = rows.map((r: UserResponse) => mapUserResponseToModel(r));
        const users = includeInactive
          ? mappedUsers
          : mappedUsers.filter((u) => u.active !== false);

        const resolvedPageSize = Array.isArray(response)
          ? payload.pageSize
          : response.page_size || response.pageSize || payload.pageSize;

        const resolvedPageNumber = Array.isArray(response)
          ? payload.pageNumber
          : (response.page_number ?? response.pageNumber ?? payload.pageNumber);

        const totalRecords = Array.isArray(response)
          ? rows.length
          : response.total_count || response.totalCount || rows.length;

        return {
          users,
          totalRecords,
          pageNumber: resolvedPageNumber,
          pageSize: resolvedPageSize,
        };
      }),
    );
  }

  searchUsers$(
    search: string,
    pageSize = 15,
    includeInactive = false,
  ): Observable<User[]> {
    return this.searchUsersPage$(search, 0, pageSize, includeInactive).pipe(
      map((page) => page.users),
    );
  }

  userSearchDataLoader: DataLoader<User> = async (request: PageRequest) => {
    const search =
      (request.filter?.['search'] as string | undefined)?.trim() || '';
    const hasSearch = search.length > 0;
    const sortBy =
      request.sortColumn &&
      ['firstName', 'lastName', 'email'].includes(request.sortColumn)
        ? request.sortColumn
        : 'firstName';
    const sortDirection =
      request.sortDirection && request.sortDirection !== 'indet'
        ? request.sortDirection
        : 'asc';

    const payload = {
      pageNumber: request.pageNumber ?? 0,
      pageSize: request.pageSize || 15,
      sortBy,
      sortDirection,
      ...(hasSearch
        ? {
            firstName: search,
            lastName: search,
            email: search,
          }
        : {}),
    };

    const response = await firstValueFrom(
      this.http.post<UserSearchApiResponse>('user/search', payload),
    );

    const rows: UserResponse[] = Array.isArray(response)
      ? response
      : response.items || response.data || [];

    const pageSize = Array.isArray(response)
      ? payload.pageSize
      : response.page_size || response.pageSize || payload.pageSize;

    const pageNumber = Array.isArray(response)
      ? payload.pageNumber
      : (response.page_number ?? response.pageNumber ?? payload.pageNumber);

    const totalRecords = Array.isArray(response)
      ? rows.length
      : response.total_count || response.totalCount || rows.length;

    return {
      data: rows
        .map((r: UserResponse) => mapUserResponseToModel(r))
        .filter((u) => u.active !== false),
      totalRecords,
      pageNumber,
      pageSize,
    };
  };

  downloadUsers(payload: {
    pageNumber: number;
    pageSize: number;
    sortBy: string;
    sortDirection: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }): Observable<HttpResponse<Blob>> {
    return this.http.post('user/download', payload, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  syncUsers() {
    return this.http.post('user/sync', {});
  }

  deleteUser(user: User) {
    // TODO: Check for new API requirements for deleting users
    const updatedUser: UserParams = {
      first_name: user.firstName,
      last_name: user.lastName,
      email: user.email,
      is_admin: user.isAdmin,
      is_linc_user: user.isLincUser,
      business_entity_codes: user.entities,
      permission_ids: user.permissions,
    };

    return this.http.put<UserUpdateResponse>('user', updatedUser);
  }

  saveUser(user: User) {
    return this.http.put<UserUpdateResponse>(
      this.USER_API_URL,
      mapUserToParams(user),
    );
  }

  createUser(user: User) {
    return this.http.post<UserUpdateResponse>(
      this.USER_API_URL,
      mapUserToParams(user),
    );
  }
}